import type { CinputshellProps } from '../Cinputshell';
import { startsWithHebrew } from '../Cmarkdown/markdown-hebrew';
import { TextInputMode, type CtextinputProps } from './Ctextinput.types';
import {
  CTEXTINPUT_CHEVRON_DISABLED_OPACITY,
  CTEXTINPUT_CHEVRON_ENABLED_OPACITY,
  CTEXTINPUT_DROPDOWN_JUSTIFY_CONTENT,
  CTEXTINPUT_DROPDOWN_PLACEHOLDER_OPACITY,
  CTEXTINPUT_SELECT_LABEL_PREFIX,
  CTEXTINPUT_SELECT_PLACEHOLDER,
  CTEXTINPUT_TEXT_ACTIVE_OPACITY,
  CTEXTINPUT_TEXT_DISABLED_OPACITY,
  CTEXTINPUT_TEXT_RESTING_OPACITY,
  CTEXTINPUT_TEXTUAL_JUSTIFY_CONTENT,
  CTEXTINPUT_TEXTAREA_EXTRA_HEIGHT_REM,
  CTEXTINPUT_TEXTAREA_ROW_HEIGHT_REM,
} from './Ctextinput.constants';
import {
  getCtextinputStyleModel,
  type CtextinputStyleModel,
} from './Ctextinput.styles';

export interface CtextinputViewModel {
  mode: TextInputMode;
  chevronOpacity: number;
  dropdownDisplayValue: string;
  dropdownTextOpacity: number;
  isDropdownMode: boolean;
  isError: boolean;
  isMultiLine: boolean;
  isShellFocused: boolean;
  justifyContent: CinputshellProps['justifyContent'];
  selectedKey: string | null;
  styles: CtextinputStyleModel;
  textOpacity: number;
  textareaHeight?: string;
  visibleErrorText: string | null;
}

interface GetCtextinputViewModelInput {
  customWrapperStyle?: CtextinputProps['style'];
  disabled: boolean;
  errorText?: string;
  fontSize?: number;
  inputContentStyle?: CtextinputProps['inputContentStyle'];
  inputElementStyle?: CtextinputProps['inputElementStyle'];
  isDropdownOpen: boolean;
  isErrorProp: boolean;
  isFocused: boolean;
  label?: string;
  mode: TextInputMode;
  padding?: string;
  paddingRight?: number;
  placeholder: string;
  rows: number;
  textAlign?: CtextinputProps['textAlign'];
  value: string;
}

interface TextOpacityInput {
  disabled: boolean;
  isError: boolean;
  isFocused: boolean;
}

interface TextareaHeightInput {
  isMultiLine: boolean;
  rows: number;
}

interface DropdownDisplayValueInput {
  isDropdownOpen: boolean;
  label?: string;
  placeholder: string;
  value: string;
}

interface JustifyContentInput {
  isDropdownMode: boolean;
}

interface IsDropdownModeInput {
  mode: TextInputMode;
}

interface IsErrorInput {
  errorText?: string;
  isErrorProp: boolean;
}

interface IsMultiLineInput {
  rows: number;
}

interface IsShellFocusedInput {
  isDropdownOpen: boolean;
  isFocused: boolean;
}

interface VisibleErrorTextInput {
  errorText?: string;
  isDropdownMode: boolean;
  isError: boolean;
}

interface DropdownTextOpacityInput {
  value: string;
}

interface SelectedKeyInput {
  value: string;
}

interface ChevronOpacityInput {
  disabled: boolean;
}

interface DropdownPlaceholderInput {
  label?: string;
  placeholder: string;
}

interface LabelPlaceholderInput {
  label?: string;
}

interface ShouldShowDropdownPlaceholderInput {
  isDropdownOpen: boolean;
  value: string;
}

export function getCtextinputViewModel({
  customWrapperStyle,
  disabled,
  errorText,
  fontSize,
  inputContentStyle,
  inputElementStyle,
  isDropdownOpen,
  isErrorProp,
  isFocused,
  label,
  mode,
  padding,
  paddingRight,
  placeholder,
  rows,
  textAlign,
  value,
}: GetCtextinputViewModelInput): CtextinputViewModel {
  const isDropdownMode = getIsDropdownMode({ mode });
  const isError = getIsError({ errorText, isErrorProp });
  const isMultiLine = getIsMultiLine({ rows });
  const isHebrew = startsWithHebrew(value);
  const isShellFocused = getIsShellFocused({ isDropdownOpen, isFocused });

  return {
    mode,
    chevronOpacity: getChevronOpacity({ disabled }),
    dropdownDisplayValue: getDropdownDisplayValue({
      isDropdownOpen,
      label,
      placeholder,
      value,
    }),
    dropdownTextOpacity: getDropdownTextOpacity({ value }),
    isDropdownMode,
    isError,
    isMultiLine,
    isShellFocused,
    justifyContent: getJustifyContent({ isDropdownMode }),
    selectedKey: getSelectedKey({ value }),
    styles: getCtextinputStyleModel({
      customWrapperStyle,
      disabled,
      fontSize,
      inputContentStyle,
      inputElementStyle,
      isDropdownOpen,
      isHebrew,
      isMultiLine,
      label,
      mode,
      padding,
      paddingRight,
      textAlign,
    }),
    textOpacity: getTextOpacity({ disabled, isError, isFocused }),
    textareaHeight: getTextareaHeight({ isMultiLine, rows }),
    visibleErrorText: getVisibleErrorText({ errorText, isDropdownMode, isError }),
  };
}

function getTextOpacity({
  disabled,
  isError,
  isFocused,
}: TextOpacityInput): number {
  if (disabled) {
    return CTEXTINPUT_TEXT_DISABLED_OPACITY;
  }

  if (isError || isFocused) {
    return CTEXTINPUT_TEXT_ACTIVE_OPACITY;
  }

  return CTEXTINPUT_TEXT_RESTING_OPACITY;
}

function getTextareaHeight({
  isMultiLine,
  rows,
}: TextareaHeightInput): string | undefined {
  if (!isMultiLine) {
    return undefined;
  }

  return `${rows * CTEXTINPUT_TEXTAREA_ROW_HEIGHT_REM + CTEXTINPUT_TEXTAREA_EXTRA_HEIGHT_REM}rem`;
}

function getDropdownDisplayValue({
  isDropdownOpen,
  label,
  placeholder,
  value,
}: DropdownDisplayValueInput): string {
  if (!shouldShowDropdownPlaceholder({ isDropdownOpen, value })) {
    return value;
  }

  return getDropdownPlaceholder({ label, placeholder });
}

function getJustifyContent({
  isDropdownMode,
}: JustifyContentInput): CinputshellProps['justifyContent'] {
  return isDropdownMode
    ? CTEXTINPUT_DROPDOWN_JUSTIFY_CONTENT
    : CTEXTINPUT_TEXTUAL_JUSTIFY_CONTENT;
}

function getIsDropdownMode({ mode }: IsDropdownModeInput): boolean {
  return mode === TextInputMode.Dropdown;
}

function getIsError({ errorText, isErrorProp }: IsErrorInput): boolean {
  return Boolean(errorText) || isErrorProp;
}

function getIsMultiLine({ rows }: IsMultiLineInput): boolean {
  return rows > 1;
}

function getIsShellFocused({
  isDropdownOpen,
  isFocused,
}: IsShellFocusedInput): boolean {
  return isFocused || isDropdownOpen;
}

function getVisibleErrorText({
  errorText,
  isDropdownMode,
  isError,
}: VisibleErrorTextInput): string | null {
  if (!isError) {
    return null;
  }

  if (!errorText) {
    return null;
  }

  if (isDropdownMode) {
    return null;
  }

  return errorText;
}

function getDropdownTextOpacity({
  value,
}: DropdownTextOpacityInput): number {
  if (value) {
    return CTEXTINPUT_TEXT_ACTIVE_OPACITY;
  }

  return CTEXTINPUT_DROPDOWN_PLACEHOLDER_OPACITY;
}

function getSelectedKey({ value }: SelectedKeyInput): string | null {
  return value || null;
}

function getChevronOpacity({ disabled }: ChevronOpacityInput): number {
  if (disabled) {
    return CTEXTINPUT_CHEVRON_DISABLED_OPACITY;
  }

  return CTEXTINPUT_CHEVRON_ENABLED_OPACITY;
}

function shouldShowDropdownPlaceholder({
  isDropdownOpen,
  value,
}: ShouldShowDropdownPlaceholderInput): boolean {
  return isDropdownOpen || !value;
}

function getDropdownPlaceholder({
  label,
  placeholder,
}: DropdownPlaceholderInput): string {
  return (
    [placeholder, getLabelPlaceholder({ label }), CTEXTINPUT_SELECT_PLACEHOLDER].find(
      isFilledString,
    ) ?? CTEXTINPUT_SELECT_PLACEHOLDER
  );
}

function getLabelPlaceholder({ label }: LabelPlaceholderInput): string | undefined {
  if (!label) {
    return undefined;
  }

  return `${CTEXTINPUT_SELECT_LABEL_PREFIX}${label}`;
}

function isFilledString(value: string | undefined): value is string {
  return Boolean(value);
}
